'use client';

import React, { useState, useEffect } from 'react';

export type ToastType = 'info' | 'warning' | 'success' | 'error';

export interface Toast {
  id: string;
  title: string;
  message: string;
  type: ToastType;
  duration?: number;
  icon?: React.ReactNode;
}

interface ToastProps {
  toast: Toast;
  onRemove: (id: string) => void;
}

export const ToastComponent: React.FC<ToastProps> = ({ toast, onRemove }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    // Trigger entrance animation
    setTimeout(() => setIsVisible(true), 10);

    if (toast.duration) {
      const timer = setTimeout(() => {
        setIsExiting(true);
        setTimeout(() => onRemove(toast.id), 150);
      }, toast.duration);

      return () => clearTimeout(timer);
    }
  }, [toast.duration, toast.id, onRemove]);

  const getToastStyles = () => {
    switch (toast.type) {
      case 'warning':
        return {
          bg: 'bg-gradient-to-r from-amber-100 to-yellow-100 dark:from-amber-800/80 dark:to-yellow-800/80',
          border: 'border-amber-300 dark:border-amber-600',
          icon: 'text-amber-600',
          title: 'text-amber-900 dark:text-amber-100',
          message: 'text-amber-800 dark:text-amber-200',
          progress: 'bg-amber-500',
        };
      case 'success':
        return {
          bg: 'bg-gradient-to-r from-green-100 to-emerald-100 dark:from-green-800/80 dark:to-emerald-800/80',
          border: 'border-green-300 dark:border-green-600',
          icon: 'text-green-600',
          title: 'text-green-900 dark:text-green-100',
          message: 'text-green-800 dark:text-green-200',
          progress: 'bg-green-500',
        };
      case 'error':
        return {
          bg: 'bg-gradient-to-r from-red-100 to-rose-100 dark:from-red-800/80 dark:to-rose-800/80',
          border: 'border-red-300 dark:border-red-600',
          icon: 'text-red-600',
          title: 'text-red-900 dark:text-red-100',
          message: 'text-red-800 dark:text-red-200',
          progress: 'bg-red-500',
        };
      default: // 'info'
        return {
          bg: 'bg-gradient-to-r from-blue-100 to-indigo-100 dark:from-blue-800/80 dark:to-indigo-800/80',
          border: 'border-blue-300 dark:border-blue-600',
          icon: 'text-blue-600',
          title: 'text-blue-900 dark:text-blue-100',
          message: 'text-blue-800 dark:text-blue-200',
          progress: 'bg-blue-500',
        };
    }
  };

  const styles = getToastStyles();

  const defaultIcons = {
    warning: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
        <path
          fillRule="evenodd"
          d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
          clipRule="evenodd"
        />
      </svg>
    ),
    success: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
        <path
          fillRule="evenodd"
          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
          clipRule="evenodd"
        />
      </svg>
    ),
    error: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
        <path
          fillRule="evenodd"
          d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
          clipRule="evenodd"
        />
      </svg>
    ),
    info: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
        <path
          fillRule="evenodd"
          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
          clipRule="evenodd"
        />
      </svg>
    ),
  };

  return (
    <div
      className={`
        relative w-full max-w-sm rounded-xl border-2 p-4 shadow-2xl backdrop-blur-sm
        transform transition-all duration-300 ease-out
        ${styles.bg} ${styles.border}
        ${isVisible && !isExiting ? 'translate-x-0 opacity-100 scale-100' : 'translate-x-full opacity-0 scale-95'}
      `}
      style={{
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 10px 25px -5px rgba(0, 0, 0, 0.1)',
      }}
    >
      {/* Progress bar */}
      {toast.duration && (
        <div
          className={`absolute top-0 left-0 h-1 ${styles.progress} rounded-t-xl origin-left transition-transform ease-linear`}
          style={{
            transform: isVisible ? 'scaleX(0)' : 'scaleX(1)',
            transitionDuration: `${toast.duration}ms`,
          }}
        />
      )}

      <div className="flex items-start space-x-3">
        {/* Icon */}
        <div className={`flex-shrink-0 ${styles.icon}`}>{toast.icon || defaultIcons[toast.type]}</div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <h4 className={`text-sm font-semibold ${styles.title}`}>{toast.title}</h4>
          <p className={`mt-1 text-sm ${styles.message}`}>{toast.message}</p>
        </div>

        {/* Close button */}
        <button
          onClick={() => {
            setIsExiting(true);
            setTimeout(() => onRemove(toast.id), 150);
          }}
          className={`
            flex-shrink-0 p-1 rounded-lg transition-colors duration-200
            ${styles.icon} hover:bg-black/5 dark:hover:bg-white/5
          `}
        >
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </button>
      </div>
    </div>
  );
};

interface ToastContainerProps {
  toasts: Toast[];
  onRemoveToast: (id: string) => void;
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center' | 'bottom-center';
}

export const ToastContainer: React.FC<ToastContainerProps> = ({ toasts, onRemoveToast, position = 'top-right' }) => {
  const getPositionClasses = () => {
    switch (position) {
      case 'top-left':
        return 'top-16 left-4';
      case 'top-center':
        return 'top-16 left-1/2 transform -translate-x-1/2';
      case 'top-right':
        return 'top-16 right-4';
      case 'bottom-left':
        return 'bottom-4 left-4';
      case 'bottom-center':
        return 'bottom-4 left-1/2 transform -translate-x-1/2';
      case 'bottom-right':
        return 'bottom-4 right-4';
      default:
        return 'top-16 right-4';
    }
  };

  return (
    <div className={`fixed z-50 ${getPositionClasses()}`}>
      <div className="space-y-3">
        {toasts.map((toast) => (
          <ToastComponent key={toast.id} toast={toast} onRemove={onRemoveToast} />
        ))}
      </div>
    </div>
  );
};
